import ReactApexChart from "react-apexcharts";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { useThemeStore } from "../../../store";
import { useTheme } from "../../../hooks";
import { themes } from "../../../config/thems";
import { useMemo } from "react";
import Utility from "../../../helper/utilityHelper";
import LottieEmptyState from "./no-data-animation/index";

const ProjectBudget = ({ bookingSummary, height = 200 }) => {
  const { theme: config } = useThemeStore();
  const { theme: mode } = useTheme();
  const theme = themes.find((theme) => theme.name === config);

  // Parse the JSON string data
  const parsedData = useMemo(() => {
    if (!bookingSummary) return {};
    
    try {
      let result;
      if (typeof bookingSummary === 'string') {
        result = JSON.parse(bookingSummary);
      } else {
        result = bookingSummary;
      }
      return result;
    } catch (error) {
      console.error('Error parsing projectBudget data:', error);
      return {};
    }
  }, [bookingSummary]);

  // Calculate total earnings
  const totalEarnings =
      (parsedData?.appEarnings || 0) +
      (parsedData?.clubEarnings || 0) +
      (parsedData?.myAppEarnings || 0) +
      (parsedData?.webEarnings || 0) +
      (parsedData?.whatsappEarnings || 0);

  // Process data for donut chart - same logic as dashboardOld
  const series = useMemo(() => {
    const appEarnings = parsedData?.appEarnings || 0;
    const clubEarnings = parsedData?.clubEarnings || 0;
    const webEarnings = parsedData?.webEarnings || 0;
    const whatsappEarnings = parsedData?.whatsappEarnings || 0;
    const myAppEarnings = parsedData?.myAppEarnings || 0;
    
    const dataArray = [appEarnings, clubEarnings];

    // Add My App earnings if available
    if (parsedData?.myAppEarnings !== null && parsedData?.myAppEarnings !== undefined && global?.myClub?.isownapp) {
      dataArray.push(myAppEarnings);
    }
    
    // Add Web earnings if available
    if (parsedData?.webEarnings !== null && parsedData?.webEarnings !== undefined && global?.myClub?.isownweb) {
      dataArray.push(webEarnings);
    }
    
    // Add WhatsApp earnings if available
    if (parsedData?.whatsappEarnings !== null && parsedData?.whatsappEarnings !== undefined && global?.myClub?.isownwhatsapp) {
      dataArray.push(whatsappEarnings);
    }
    
    return dataArray;
  }, [parsedData]);

  const labels = useMemo(() => {
    const labelArray = ["App", "Club"];
    
    // Add My App if available
    if (parsedData?.myAppEarnings !== null && parsedData?.myAppEarnings !== undefined && global?.myClub?.isownapp) {
      labelArray.push("My App");
    }
    
    // Add Web if available
    if (parsedData?.webEarnings !== null && parsedData?.webEarnings !== undefined && global?.myClub?.isownweb) {
      labelArray.push("Web");
    }
    
    // Add WhatsApp if available
    if (parsedData?.whatsappEarnings !== null && parsedData?.whatsappEarnings !== undefined && global?.myClub?.isownwhatsapp) {
      labelArray.push("WhatsApp");
    }
    
    return labelArray;
  }, [parsedData]);

  const options = {
    chart: {
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      pie: {
        startAngle: -90,
        endAngle: 270,
        donut: {
          size: '70%',
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      type: "solid",
    },
    colors: [
      `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].primary})`, // App - Blue
      `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].info})`, // Club - Cyan
      `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].success})`, // Web - Green
      `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].warning})`, // My App - Orange
      `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].secondary})`, // WhatsApp - Purple
    ],
    legend: {
      position: "bottom",
      formatter: function (val, opts) {
        const value = opts.w.globals.series[opts.seriesIndex];
        return val + " - " + Utility.formatNumber(value);
      },
      labels: {
        colors: `hsl(${
          theme?.cssVars[
            mode === "dark" || mode === "system" ? "dark" : "light"
          ].chartLabel
        })`,
      },
    },
    stroke: {
      width: 0,
    },
    tooltip: {
      theme: mode === "dark" || mode === "system" ? "dark" : "light",
      y: {
        formatter: function (val) {
          return Utility.formatNumberWithCommas(val);
        },
      },
    },
    labels: labels,
    padding: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  };

  // Check if there's no data to display
  const hasNoData = totalEarnings === 0;

  return (
    <Card className="h-full overflow-visible">
      <CardHeader className="flex-row justify-between items-center mb-0 border-none pt-4 md:pt-8 pl-3 md:pl-6 overflow-visible">
        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 relative w-full overflow-visible">
          <CardTitle className="text-lg md:text-2xl font-medium">
            Earnings ({Utility.formatNumber(totalEarnings)})
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="px-0 pb-6">
        {hasNoData ? (
          <LottieEmptyState
            title="No Earnings Data"
            subtitle="Earnings information will appear here when available"
            height={320}
            width={200}
          />
        ) : (
          <div className="w-full max-w-[300px] mx-auto">
            <ReactApexChart
              options={options}
              series={series}
              type="donut"
              height={320}
              width={"100%"}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProjectBudget;